import { angleDelta, clamp } from '../core/math';

/**
 * On-foot movement maths, pure and DOM-free (footShaping.test.ts). The player system integrates these
 * every fixed step; the speeds, the shaping of the stick's push and the body's turn live here so they
 * are unit-tested on their own.
 *
 *  - The stick (or WASD / a pad) gives a stepless walk → jog: the target speed follows the deflection
 *    up to JOG_SPEED, so the run gait is reachable without RUN.
 *  - Sprinting happens ONLY with RUN held (Shift / the pad button / the touch RUN knob): any push past
 *    the dead zone then gives the full RUN_SPEED, whatever the deflection, in the push's direction.
 *  - Starting, stopping and turning back are slew-limited at different rates (a soft start, a quick
 *    stop that still plants), and the body turns through a damped approach instead of snapping.
 *
 * RUN_SPEED mirrors `FOOT_MAX_SPEED` in shared/limits.ts (the online server's cap; checked by
 * shared/limits.client.test.ts): nothing here may push the player faster.
 */

/** Full stick without RUN: a jog (m/s). Just under footstepFx's dust threshold: only a sprint kicks up dust. */
export const JOG_SPEED = 4.4;
/** RUN held: the sprint (m/s). Equals shared/limits.ts FOOT_MAX_SPEED. */
export const RUN_SPEED = 6.6;
/** Slowest deliberate walk (m/s), just outside the dead zone: no creeping that reads as skating. */
export const WALK_MIN_SPEED = 0.9;
/** Deflection (0..1) under which the stick counts as let go. */
export const MOVE_DEAD = 0.1;
/** Shape of the walk → jog curve: >1 keeps more of the travel for walking paces. */
const JOG_CURVE = 1.25;

// Ramps stay under ~0.3 s (docs/research touch-driving: longer reads as lag in a fight; the feel comes from the
// stick filter and the damped turn, not from slow physics).
/** Acceleration (m/s²) building up to a jog: ~0.29 s from standing. */
export const FOOT_ACCEL = 15;
/** Acceleration while sprinting: ~0.30 s to full speed. */
export const SPRINT_ACCEL = 22;
/** Deceleration when the stick is let go: a jog stops in ~0.18 s, a sprint in ~0.28 s. */
export const FOOT_STOP = 24;
/** Slew when the push opposes the current velocity (turning back / dodging): the wrong-way movement is gone in ~0.16 s. */
export const FOOT_TURNBACK = 28;

/** Body turn: damping rate (per s) at a light push and at the rim, and the hard cap (rad/s). */
export const TURN_DAMP_MIN = 6;
export const TURN_DAMP_MAX = 12;
export const TURN_MAX_RATE = 14;

/**
 * Target ground speed (m/s) for a push of `mag` (0..1, after the dead zone). Without RUN the curve is
 * continuous from WALK_MIN_SPEED just outside the dead zone to JOG_SPEED at the rim; with RUN held any
 * push past the dead zone is the full sprint.
 */
export function footTargetSpeed(mag: number, running: boolean): number {
  if (!(mag > MOVE_DEAD)) return 0;
  if (running) return RUN_SPEED;
  const t = clamp((mag - MOVE_DEAD) / (1 - MOVE_DEAD), 0, 1);
  return WALK_MIN_SPEED + (JOG_SPEED - WALK_MIN_SPEED) * Math.pow(t, JOG_CURVE);
}

/**
 * Slew limit (m/s²) for this step: stopping when the stick is let go, turning back when the target
 * opposes the current velocity, otherwise building up at the jog or sprint rate.
 */
export function footSlewRate(mag: number, running: boolean, velX: number, velZ: number, targetX: number, targetZ: number): number {
  if (!(mag > MOVE_DEAD)) return FOOT_STOP;
  if (velX * targetX + velZ * targetZ < 0) return FOOT_TURNBACK;
  return running ? SPRINT_ACCEL : FOOT_ACCEL;
}

/**
 * One slew-limited step of the velocity towards the target: the change is capped at `rate * dt`, so
 * the start is soft and a stop still plants the feet within a few frames.
 */
export function slewVelocity(velX: number, velZ: number, targetX: number, targetZ: number, rate: number, dt: number): { x: number; z: number } {
  const dvx = targetX - velX;
  const dvz = targetZ - velZ;
  const dl = Math.hypot(dvx, dvz);
  const maxDv = rate * dt;
  const k = dl > maxDv ? maxDv / dl : 1;
  return { x: velX + dvx * k, z: velZ + dvz * k };
}

/**
 * The body turning to face `target` (rad): a damped approach whose rate grows with the push (a light
 * push turns gently, a push to the rim is quick), capped at TURN_MAX_RATE so a reversal still takes a
 * few frames. Returns the new heading.
 */
export function turnHeading(heading: number, target: number, mag: number, dt: number): number {
  const d = angleDelta(heading, target);
  const damp = TURN_DAMP_MIN + (TURN_DAMP_MAX - TURN_DAMP_MIN) * clamp(mag, 0, 1);
  const step = d * (1 - Math.exp(-damp * dt));
  return heading + clamp(step, -TURN_MAX_RATE * dt, TURN_MAX_RATE * dt);
}

/** Aim turn (facing a lock, a swing, a hip shot): damping rate (per s) and the hard cap (rad/s). */
export const AIM_TURN_DAMP = 16;
export const AIM_TURN_MAX_RATE = 12;

/**
 * The body turning onto an aim line (the lock-on target, the man a swing goes at, where a hip shot went):
 * quicker than the walking turn but still damped — a lock taking or switching no longer whips the
 * figure round in a frame (90° in ~0.17 s, a full reversal in ~0.3 s). The shot itself goes at the
 * lock, not along the body, so nothing waits on this. Returns the new heading.
 */
export function aimTurnHeading(heading: number, target: number, dt: number): number {
  const d = angleDelta(heading, target);
  const step = d * (1 - Math.exp(-AIM_TURN_DAMP * dt));
  return heading + clamp(step, -AIM_TURN_MAX_RATE * dt, AIM_TURN_MAX_RATE * dt);
}
